import { useEffect, useState } from 'react';
import { clamp } from '../../utils/clamp';
import { NumberInput } from './input/Input';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from '@boxicons/react';
import { ArrowButton } from '../ui/buttons';

interface Props {
    onPageChange: (page: number) => void;
    page: number;
    total: number;
    limit: number;
    className?: string;
}

export default function PageButtons({ onPageChange, page, total, limit, className = 'mt-3' }: Props) {
    const [pageInput, setPageInput] = useState(page + 1);
    const [pageInputKey, setPageInputKey] = useState(0);

    useEffect(() => {
        setPageInput(page + 1);
    }, [page]);

    if (total <= limit) return; // If there is only a single page, return nothing

    const maxPages = Math.ceil(total / limit);

    function onChange(page: number | null) {
        if (page === null) return;

        setPageInput(page);
        const parsed = parseInt(page.toString());
        if (isNaN(parsed)) return;

        const clamped = clamp(parsed - 1, 0, maxPages - 1);
        if (clamped !== page) {
            onPageChange(clamped);
            setPageInput(clamped + 1);
            setPageInputKey((prev) => prev + 1);
        }
    }

    function onPageButtonClick(page: number) {
        onPageChange(page);
        setPageInput(page + 1);
        setPageInputKey((prev) => prev + 1);
    }

    return (
        <div className={'flex gap-3 justify-center items-center ' + className}>
            <div className={'flex gap-2 text-2xl' + (page === 0 ? ' opacity-0 pointer-events-none' : '')}>
                <ArrowButton aria-label='First page' onClick={() => onPageButtonClick(0)}>
                    <ChevronsLeft />
                </ArrowButton>
                <ArrowButton aria-label='Previous page' onClick={() => onPageButtonClick(page - 1)}>
                    <ChevronLeft />
                </ArrowButton>
            </div>
            <div className='flex items-center gap-1'>
                <p className='m-0'>Page</p>
                <NumberInput
                    disableSpinner={true}
                    centered={true}
                    value={pageInput}
                    onChange={onChange}
                    style={{ width: `calc(${maxPages.toString().length}ch + 1.5rem)` }} // Fit the widest page number + room for input padding
                    key={pageInputKey}
                />
                <p>/{maxPages}</p>
            </div>
            <div className={'flex gap-2 text-2xl' + (page + 1 < maxPages ? '' : ' opacity-0 pointer-events-none')}>
                <ArrowButton aria-label='Next page' onClick={() => onPageButtonClick(page + 1)}>
                    <ChevronRight />
                </ArrowButton>
                <ArrowButton aria-label='Last page' onClick={() => onPageButtonClick(maxPages - 1)}>
                    <ChevronsRight />
                </ArrowButton>
            </div>
        </div>
    );
}
